'use client';

import React, { useState } from 'react';
import { ExtendedArchiMateDiagram, Scenario } from '@/lib/archimate/extended-types';
import {
  createScenario,
  compareScenarios,
  exportScenarioReport,
} from '@/lib/archimate/scenario-utils';
import { GitCompare, Plus, X, TrendingUp, TrendingDown, Minus, Download } from 'lucide-react';
import { PanelHelpDialog, HelpIconButton } from './PanelHelpDialog';

interface ScenarioPanelProps {
  diagram: ExtendedArchiMateDiagram;
  onUpdate: (diagram: ExtendedArchiMateDiagram) => void;
  onClose: () => void;
  onSelectElement: (elementId: string) => void;
}

export default function ScenarioPanel({
  diagram,
  onUpdate,
  onClose,
  onSelectElement,
}: ScenarioPanelProps) {
  const [viewMode, setViewMode] = useState<'list' | 'create' | 'view' | 'compare'>('list');
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);
  const [compareScenarioIds, setCompareScenarioIds] = useState<string[]>([]);
  const [newScenarioForm, setNewScenarioForm] = useState({
    name: '',
    description: '',
    timeframe: 'short-term' as 'short-term' | 'medium-term' | 'long-term',
  });
  const [showHelp, setShowHelp] = useState(false);

  // Helper function to handle score changes
  const handleScoreChange = (scoreKey: keyof Scenario['scores'], value: number) => {
    if (!selectedScenario) return;

    const updatedScenario = {
      ...selectedScenario,
      scores: { ...selectedScenario.scores, [scoreKey]: value },
    };
    updatedScenario.totalScore = Object.values(updatedScenario.scores).reduce((sum, score) => sum + score, 0);

    const updatedDiagram = {
      ...diagram,
      scenarios: diagram.scenarios.map((s) =>
        s.id === selectedScenario.id ? updatedScenario : s
      ),
    };
    onUpdate(updatedDiagram);
    setSelectedScenario(updatedScenario);
  };

  const handleCreateScenario = () => {
    if (!newScenarioForm.name.trim()) return;

    const newScenario = createScenario(
      newScenarioForm.name,
      newScenarioForm.description || '',
      diagram.id,
      undefined // no baseline diagram
    );

    const updatedDiagram = {
      ...diagram,
      scenarios: [...diagram.scenarios, newScenario],
    };

    onUpdate(updatedDiagram);
    setNewScenarioForm({ name: '', description: '', timeframe: 'short-term' });
    setViewMode('list');
  };

  const handleDeleteScenario = (scenarioId: string) => {
    const updatedDiagram = {
      ...diagram,
      scenarios: diagram.scenarios.filter((s) => s.id !== scenarioId),
    };

    onUpdate(updatedDiagram);
    if (selectedScenario?.id === scenarioId) {
      setSelectedScenario(null);
    }
  };

  const handleCloneScenario = (scenario: Scenario) => {
    const clonedScenario = createScenario(
      `${scenario.name} (Copy)`,
      scenario.description,
      scenario.diagramId,
      scenario.baselineDiagramId
    );

    // Copy scores and other properties
    clonedScenario.scores = { ...scenario.scores };

    const updatedDiagram = {
      ...diagram,
      scenarios: [...diagram.scenarios, clonedScenario],
    };

    onUpdate(updatedDiagram);
  };

  const handleToggleCompare = (scenarioId: string) => {
    setCompareScenarioIds((prev) => {
      if (prev.includes(scenarioId)) {
        return prev.filter((id) => id !== scenarioId);
      } else if (prev.length < 3) {
        return [...prev, scenarioId];
      }
      return prev;
    });
  };

  const handleExportReport = (scenario: Scenario) => {
    const report = exportScenarioReport([scenario]);
    const blob = new Blob([report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${scenario.name.replace(/\s+/g, '-')}-scenario-report.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderList = () => {
    return (
      <div className="space-y-4">
        {/* Create New Button */}
        <button
          onClick={() => setViewMode('create')}
          className="w-full px-4 py-3 bg-accent-muted hover:bg-accent-muted border border-border text-accent rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Create New Scenario
        </button>

        {/* Compare Mode Toggle */}
        {diagram.scenarios.length >= 2 && (
          <div className="flex items-center justify-between p-3 bg-cream/5 rounded-lg">
            <div className="text-sm text-foreground-secondary">
              Compare Mode {compareScenarioIds.length > 0 && `(${compareScenarioIds.length} selected)`}
            </div>
            <div className="flex gap-2">
              {compareScenarioIds.length >= 2 && (
                <button
                  onClick={() => setViewMode('compare')}
                  className="px-3 py-1 text-xs bg-accent hover:bg-accent-hover text-accent-foreground font-medium rounded transition-colors"
                >
                  Compare
                </button>
              )}
              {compareScenarioIds.length > 0 && (
                <button
                  onClick={() => setCompareScenarioIds([])}
                  className="px-3 py-1 text-xs bg-cream/10 hover:bg-cream/20 text-foreground rounded transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}

        {/* Scenario List */}
        {diagram.scenarios.length === 0 ? (
          <div className="p-8 text-center text-foreground-tertiary text-sm">
            No scenarios defined yet. Create a scenario to explore what-if alternatives.
          </div>
        ) : (
          <div className="space-y-3">
            {diagram.scenarios.map((scenario) => {
              const isSelected = compareScenarioIds.includes(scenario.id);

              return (
                <div
                  key={scenario.id}
                  className={`p-4 rounded-lg border transition-all ${
                    isSelected
                      ? 'bg-accent-muted border-accent'
                      : 'bg-cream/5 border-border hover:bg-cream/10'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-foreground">{scenario.name}</span>
                      </div>
                      {scenario.description && (
                        <div className="text-sm text-foreground-secondary mb-2">{scenario.description}</div>
                      )}
                      <div className="flex items-center gap-4 text-xs text-foreground-tertiary">
                        <span>Score: {scenario.totalScore}/100</span>
                        <span>Cost: {scenario.scores.cost}/10</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {diagram.scenarios.length >= 2 && (
                        <button
                          onClick={() => handleToggleCompare(scenario.id)}
                          className={`p-2 rounded transition-colors ${
                            isSelected
                              ? 'bg-accent text-accent-foreground'
                              : 'hover:bg-cream/10 text-foreground-tertiary'
                          }`}
                          title="Toggle for comparison"
                        >
                          <GitCompare className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setSelectedScenario(scenario);
                          setViewMode('view');
                        }}
                        className="px-3 py-1 text-xs bg-cream/10 hover:bg-cream/20 text-foreground rounded transition-colors"
                      >
                        View
                      </button>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  {(scenario.cons.length > 0 || scenario.pros.length > 0) && (
                    <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-border-muted">
                      {scenario.cons.length > 0 && (
                        <div className="flex items-center gap-2 text-xs text-red-400">
                          <TrendingDown className="w-3 h-3" />
                          {scenario.cons.length} con{scenario.cons.length !== 1 ? 's' : ''}
                        </div>
                      )}
                      {scenario.pros.length > 0 && (
                        <div className="flex items-center gap-2 text-xs text-green-400">
                          <TrendingUp className="w-3 h-3" />
                          {scenario.pros.length} pro{scenario.pros.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderCreate = () => {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-cream/5 border border-border rounded-lg space-y-4">
          <h3 className="text-sm font-medium text-foreground">Create New Scenario</h3>

          <div className="space-y-2">
            <label className="text-sm text-foreground-secondary">Name</label>
            <input
              type="text"
              value={newScenarioForm.name}
              onChange={(e) => setNewScenarioForm({ ...newScenarioForm, name: e.target.value })}
              placeholder="e.g., Cloud Migration, Microservices Split"
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded focus:outline-none focus:border-accent"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm text-foreground-secondary">Description</label>
            <textarea
              value={newScenarioForm.description}
              onChange={(e) =>
                setNewScenarioForm({ ...newScenarioForm, description: e.target.value })
              }
              placeholder="Describe this scenario..."
              rows={4}
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded focus:outline-none focus:border-accent resize-none"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm text-foreground-secondary">Timeframe</label>
            <select
              value={newScenarioForm.timeframe}
              onChange={(e) =>
                setNewScenarioForm({
                  ...newScenarioForm,
                  timeframe: e.target.value as any,
                })
              }
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded focus:outline-none focus:border-accent"
            >
              <option value="short-term">Short-term (0-6 months)</option>
              <option value="medium-term">Medium-term (6-18 months)</option>
              <option value="long-term">Long-term (18+ months)</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleCreateScenario}
              disabled={!newScenarioForm.name.trim()}
              className="flex-1 px-4 py-2 bg-accent hover:bg-accent-hover text-accent-foreground font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create Scenario
            </button>
            <button
              onClick={() => {
                setViewMode('list');
                setNewScenarioForm({ name: '', description: '', timeframe: 'short-term' });
              }}
              className="px-4 py-2 bg-cream/10 hover:bg-cream/20 text-foreground rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>

        <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg text-sm text-foreground-secondary">
          <div className="font-medium text-blue-400 mb-2">Next Steps</div>
          <div className="text-xs text-foreground-tertiary space-y-1">
            <div>• After creating, you&apos;ll add specific changes to elements</div>
            <div>• Document risks and benefits</div>
            <div>• Estimate costs and timeline</div>
            <div>• Compare with other scenarios</div>
          </div>
        </div>
      </div>
    );
  };

  const renderView = () => {
    if (!selectedScenario) return null;

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="p-4 bg-cream/5 rounded-lg">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex-1">
              <h3 className="text-lg font-medium text-foreground mb-1">{selectedScenario.name}</h3>
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-cream/10 text-foreground-secondary text-xs rounded">
                  Total Score: {selectedScenario.totalScore}/100
                </span>
              </div>
            </div>

      {/* Help Dialog */}
      {showHelp && (
        <PanelHelpDialog
          panelKey="scenario"
          onClose={() => setShowHelp(false)}
        />
      )}
            <div className="flex gap-2">
              <button
                onClick={() => handleExportReport(selectedScenario)}
                className="p-2 hover:bg-cream/10 rounded transition-colors"
                title="Export Report"
              >
                <Download className="w-4 h-4 text-foreground" />
              </button>
              <button
                onClick={() => handleCloneScenario(selectedScenario)}
                className="px-3 py-1 text-xs bg-cream/10 hover:bg-cream/20 text-foreground rounded transition-colors"
              >
                Clone
              </button>
              <button
                onClick={() => handleDeleteScenario(selectedScenario.id)}
                className="px-3 py-1 text-xs bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
          {selectedScenario.description && (
            <div className="text-sm text-foreground-secondary">{selectedScenario.description}</div>
          )}
        </div>

        {/* Scores */}
        <div className="p-4 bg-cream/5 rounded-lg">
          <h4 className="text-sm font-medium text-foreground mb-3">Evaluation Scores</h4>
          <div className="space-y-4">
            {/* Cost */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-foreground-tertiary">Cost</label>
                <span className="text-sm font-medium text-foreground">{selectedScenario.scores.cost}/10</span>
              </div>
              <input
                type="range"
                min="1"
                max="10"
                value={selectedScenario.scores.cost}
                onChange={(e) => handleScoreChange('cost', parseInt(e.target.value))}
                className="w-full h-2 bg-cream/20 rounded-lg appearance-none cursor-pointer accent-orange"
              />
            </div>

            {/* Complexity */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-foreground-tertiary">Complexity</label>
                <span className="text-sm font-medium text-foreground">{selectedScenario.scores.complexity}/10</span>
              </div>
              <input
                type="range"
                min="1"
                max="10"
                value={selectedScenario.scores.complexity}
                onChange={(e) => handleScoreChange('complexity', parseInt(e.target.value))}
                className="w-full h-2 bg-cream/20 rounded-lg appearance-none cursor-pointer accent-orange"
              />
            </div>

            {/* Risk */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-foreground-tertiary">Risk</label>
                <span className="text-sm font-medium text-foreground">{selectedScenario.scores.risk}/10</span>
              </div>
              <input
                type="range"
                min="1"
                max="10"
                value={selectedScenario.scores.risk}
                onChange={(e) => handleScoreChange('risk', parseInt(e.target.value))}
                className="w-full h-2 bg-cream/20 rounded-lg appearance-none cursor-pointer accent-orange"
              />
            </div>

            {/* Time to Market */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-foreground-tertiary">Time to Market</label>
                <span className="text-sm font-medium text-foreground">{selectedScenario.scores.timeToMarket}/10</span>
              </div>
              <input
                type="range"
                min="1"
                max="10"
                value={selectedScenario.scores.timeToMarket}
                onChange={(e) => handleScoreChange('timeToMarket', parseInt(e.target.value))}
                className="w-full h-2 bg-cream/20 rounded-lg appearance-none cursor-pointer accent-orange"
              />
            </div>

            {/* Scalability */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-foreground-tertiary">Scalability</label>
                <span className="text-sm font-medium text-foreground">{selectedScenario.scores.scalability}/10</span>
              </div>
              <input
                type="range"
                min="1"
                max="10"
                value={selectedScenario.scores.scalability}
                onChange={(e) => handleScoreChange('scalability', parseInt(e.target.value))}
                className="w-full h-2 bg-cream/20 rounded-lg appearance-none cursor-pointer accent-orange"
              />
            </div>

            {/* Maintainability */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-foreground-tertiary">Maintainability</label>
                <span className="text-sm font-medium text-foreground">{selectedScenario.scores.maintainability}/10</span>
              </div>
              <input
                type="range"
                min="1"
                max="10"
                value={selectedScenario.scores.maintainability}
                onChange={(e) => handleScoreChange('maintainability', parseInt(e.target.value))}
                className="w-full h-2 bg-cream/20 rounded-lg appearance-none cursor-pointer accent-orange"
              />
            </div>
          </div>
        </div>

        {/* Assumptions */}
        {selectedScenario.assumptions.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">Assumptions</h4>
            <div className="space-y-2">
              {selectedScenario.assumptions.map((assumption, index) => (
                <div key={index} className="p-3 bg-cream/5 rounded-lg">
                  <div className="text-sm text-foreground-secondary">{assumption}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pros */}
        {selectedScenario.pros.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-400" />
              Pros
            </h4>
            <div className="space-y-2">
              {selectedScenario.pros.map((pro, index) => (
                <div key={index} className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="text-sm text-green-400">{pro}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cons */}
        {selectedScenario.cons.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-red-400" />
              Cons
            </h4>
            <div className="space-y-2">
              {selectedScenario.cons.map((con, index) => (
                <div key={index} className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="text-sm text-red-400">{con}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCompare = () => {
    if (compareScenarioIds.length < 2) {
      return (
        <div className="p-8 text-center text-foreground-tertiary text-sm">
          Select at least 2 scenarios to compare
        </div>
      );
    }

    const scenarios = compareScenarioIds
      .map((id) => diagram.scenarios.find((s) => s.id === id))
      .filter(Boolean) as Scenario[];

    const result = compareScenarios(scenarios);

    return (
      <div className="space-y-4">
        {/* Winner Card */}
        <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-green-400" />
            <h4 className="text-sm font-medium text-green-400">Recommended</h4>
          </div>
          <div className="text-lg font-medium text-foreground mb-1">{result.winner.name}</div>
          <div className="text-sm text-foreground-secondary">Total Score: {result.winner.totalScore}/60</div>
        </div>

        {/* Dimension-by-Dimension Comparison */}
        <div className="p-4 bg-cream/5 rounded-lg">
          <h4 className="text-sm font-medium text-foreground mb-3">Dimension Comparison</h4>
          <div className="space-y-3">
            {result.comparison.map(({ dimension, winner, scores }) => (
              <div key={dimension} className="border-b border-border-muted pb-3 last:border-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-foreground capitalize">
                    {dimension.replace(/([A-Z])/g, ' $1').trim()}
                  </div>
                  <div className="text-xs text-foreground-tertiary">{winner.name} wins</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {scores.map(({ scenario, score }) => (
                    <div
                      key={scenario.id}
                      className={`p-2 rounded text-center ${
                        scenario.id === winner.id
                          ? 'bg-green-500/10 border border-green-500/20'
                          : 'bg-cream/5'
                      }`}
                    >
                      <div className="text-xs text-foreground-tertiary">{scenario.name}</div>
                      <div className="text-sm font-medium text-foreground">{score}/10</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recommendation */}
        <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <h4 className="text-sm font-medium text-blue-400 mb-2">Analysis</h4>
          <div className="text-sm text-foreground-secondary whitespace-pre-line">{result.recommendation}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed top-0 right-0 h-full w-[600px] bg-background/95 backdrop-blur border-l border-border shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <GitCompare className="w-6 h-6 text-accent" />
          <h2 className="text-xl font-bold text-foreground">Scenarios</h2>
          <HelpIconButton onClick={() => setShowHelp(true)} />
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-cream/10 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 p-4 border-b border-border">
        <button
          onClick={() => setViewMode('list')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            viewMode === 'list' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
          }`}
        >
          List
        </button>
        {selectedScenario && (
          <button
            onClick={() => setViewMode('view')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'view' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            Details
          </button>
        )}
        {compareScenarioIds.length >= 2 && (
          <button
            onClick={() => setViewMode('compare')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'compare'
                ? 'bg-accent text-accent-foreground'
                : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            Compare
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {viewMode === 'list' && renderList()}
        {viewMode === 'create' && renderCreate()}
        {viewMode === 'view' && renderView()}
        {viewMode === 'compare' && renderCompare()}
      </div>
    </div>
  );
}
